import { useEffect } from 'react';

const SITE = 'FlightDeals.in';
const BASE = 'https://flightdeals.in';
const DEFAULT_DESC = 'Compare flights, hotels & experiences and book with trusted partners. Transparent FlightDeals Score, no booking fees.';
const DEFAULT_IMG = `${BASE}/og-banner.jpg`;

// Deterministic, imperative <head> manager. We upsert title/description/canonical/OG/JSON-LD
// directly in the DOM (not via a JS framework that injects async), so the build-time
// prerenderer (react-snap) reliably captures unique, server-rendered SEO tags into each
// route's static HTML — before any JS runs for crawlers. Runtime behaviour/design unchanged.
const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setRobots = (value) => upsertMeta('name', 'robots', value);

const setJsonLd = (obj) => {
  const id = 'seo-jsonld';
  let el = document.getElementById(id);
  if (!obj) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
};

export const Seo = ({
  title,
  description = DEFAULT_DESC,
  path = '',
  image = DEFAULT_IMG,
  type = 'website',
  jsonLd = null,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Compare flights, hotels & experiences`;
  const routePath = path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const canonical = (`${BASE}${routePath || ''}`).replace(/\/+$/, '') || BASE;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', canonical);
    upsertMeta('property', 'og:site_name', SITE);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    setRobots(noindex ? 'noindex,nofollow' : 'index,follow');
    setJsonLd(jsonLd);
  }, [fullTitle, description, canonical, image, type, noindex, jsonLd]);

  return null;
};

export default Seo;
